import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Sheet } from "@mui/joy";
import { ArrowBack } from "@mui/icons-material";
import { useReactMediaRecorder } from "react-media-recorder";
import CloseIcon from "../../assets/svgs/crossIcon.svg";
import CameraPreview from "./CameraPreview";
import CameraControls from "./CameraControls";
import ConfirmationModal from "../modals/ConfirmationModal";
import { useNavigate } from "react-router-dom";

interface CameraViewProps {
  onClose: () => void;
  onSubmit: (blob: Blob) => void;
  onError?: (message: string) => void;
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>;
  showBackButton?: boolean;
}

// Determine best supported MIME type for this device
const getSupportedMimeType = () => {
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4'
  ];
  
  for (const type of types) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      console.log('Using MIME type:', type);
      return type;
    }
  }
  
  console.log('Using default MIME type');
  return undefined;
};

const CameraView: React.FC<CameraViewProps> = ({
  onClose,
  onSubmit,
  onError,
  showBackButton = false,
}) => {
  const [showExitModal, setShowExitModal] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };
  
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  
  const supportedMimeType = getSupportedMimeType();
  
  const {
    startRecording,
    stopRecording,
    clearBlobUrl,
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    blobPropertyBag: supportedMimeType ? {
      type: supportedMimeType
    } : undefined,
    mediaRecorderOptions: supportedMimeType ? {
      mimeType: supportedMimeType,
      videoBitsPerSecond: 2500000, // 2.5 Mbps - good quality without excessive size
      audioBitsPerSecond: 128000,  // 128 kbps for audio
    } : undefined,
    askPermissionOnMount: true,
    onStart: () => {
      console.log('Recording started with MIME type:', supportedMimeType);
      setRecordingTime(0);
      startTimer();
    },
     onStop: (blobUrl: string, blob: Blob) => {
       console.log('Recording stopped', { 
         blobUrl, 
         blobSize: blob.size,
         blobType: blob.type,
         recordingDuration: recordingTime
       });
       
       stopTimer();
       
       // Delay stopping tracks to ensure MediaRecorder has fully finalized
       // Critical for preventing truncated video/audio on mobile
       setTimeout(() => {
         if (cameraStream) {
           cameraStream.getTracks().forEach(track => {
             track.stop();
             console.log('Stopped camera track after finalization:', track.kind, track.readyState);
           });
         }
       }, 500); // 500ms delay to ensure blob is fully written
       
       clearBlobUrl();
       
       navigate("/apply/review", { 
         state: { 
           camera: true,
           blob: blob,
           blobSize: blob.size,
           blobType: blob.type,
           timestamp: Date.now()
         } 
       });
       
       onSubmit(blob);
     },
  });
  
  useEffect(() => {
    const initializeCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        
        setCameraStream(stream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setCameraError('Failed to access camera. Please check permissions and try again.');
        if (onError) {
          onError('Failed to access camera. Please check permissions and try again.');
        }
      }
    };
    
    initializeCamera();
    
    // Cleanup function
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasRecording = !!videoBlob;

  const handleStartRecording = () => {
    setIsRecording(true);
    setVideoBlob(null);
    clearBlobUrl();
    startRecording();
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    stopRecording();
  };

  useEffect(() => {
    return () => {
      cleanupCameraResources();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (showBackButton) {
      navigate(-1);
    } else if (isRecording) {
      setShowExitModal(true);
    } else {
      cleanupAndClose();
    }
  };

  const cleanupCameraResources = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop();
        console.log('Cleanup: Stopped camera track:', track.kind);
      });
    }
    clearBlobUrl();
  };

  const cleanupAndClose = () => {
    cleanupCameraResources();
    onClose();
  };

  const handleConfirmExit = () => {
    if (isRecording) {
      stopRecording();
    }
    cleanupAndClose();
    setShowExitModal(false);
  };

  return (
    <>
      <Sheet
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "black",
          zIndex: 1300,
        }}
      >
        <Box sx={{ position: "relative", height: "100%" }}>
          <CameraPreview
            videoRef={videoRef}
            cameraError={cameraError}
            onClose={cleanupAndClose}
          />

          <Button
            onClick={handleClose}
            variant="plain"
            sx={{
              position: "absolute",
              top: "11px",
              left: "1px",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&:active": {
                backgroundColor: "transparent",
              },
            }}
          >
            {showBackButton ? (
              <Box sx={{ 
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                },
                borderRadius: "50%", 
                p: 1, 
                width: "45px", 
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                <ArrowBack
                  sx={{ 
                    fontSize: 24, 
                    color: "black",
                    pointerEvents: "none"
                  }} 
                />
              </Box>
            ) : (
              <img
                src={CloseIcon}
                alt="closeIcon"
                style={{ pointerEvents: "none" }}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            )}
          </Button>

          <CameraControls
            isRecording={isRecording}
            hasRecording={hasRecording}
            videoBlob={videoBlob}
            onStartRecording={handleStartRecording}
            onProceed={handleStopRecording}
          />
        </Box>
      </Sheet>

      <ConfirmationModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        title="Are you sure you want to exit?"
        description={
          isRecording
            ? "Your recording will be discarded if you exit."
            : "Any unsaved changes will be lost."
        }
        confirmLabel="Exit"
        confirmAction={handleConfirmExit}
        cancelLabel="Cancel"
      />
    </>
  );
};

export default CameraView;
